import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronDown, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SiteHeader } from '@/components/SiteHeader';
import { SiteFooter } from '@/components/SiteFooter';
import { ParticleCanvas } from '@/components/ParticleCanvas';
import { FrameworkCard } from '@/components/FrameworkCard';
import { EssayCard } from '@/components/EssayCard';
import { ConcentricRings } from '@/components/Motifs';
import api from '@/lib/api';
import { toast } from 'sonner';
import { usePageTitle } from '@/hooks/usePageTitle';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.1, duration: 0.7, ease: [0.22, 1, 0.36, 1] }
  })
};

export default function HomePage() {
  const [pageData, setPageData] = useState(null);
  const [frameworks, setFrameworks] = useState([]);
  const [essays, setEssays] = useState([]);
  const [product, setProduct] = useState(null);
  const [email, setEmail] = useState('');
  const [waitlistEmail, setWaitlistEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  usePageTitle();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pageRes, fwRes, essayRes, prodRes] = await Promise.all([
          api.get('/pages/home'),
          api.get('/frameworks'),
          api.get('/essays?limit=3'),
          api.get('/products')
        ]);
        setPageData(pageRes.data);
        setFrameworks(fwRes.data);
        setEssays(essayRes.data.essays || []);
        setProduct(prodRes.data[0] || null);
      } catch (err) {
        setError('Unable to load page content. Please refresh.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleNewsletter = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      const res = await api.post('/newsletter', { email });
      toast.success(res.data.message);
      setEmail('');
    } catch (err) {
      toast.error('Failed to subscribe. Please try again.');
    }
  };

  const handleWaitlist = async (e) => {
    e.preventDefault();
    if (!waitlistEmail) return;
    try {
      const res = await api.post('/waitlist', { email: waitlistEmail });
      toast.success(res.data.message);
      setWaitlistEmail('');
    } catch (err) {
      toast.error('Failed to join waitlist. Please try again.');
    }
  };

  const sections = pageData?.sections || {};
  const hero = sections.hero || {};
  const anchor = sections.identity_anchor || {};
  const pillars = sections.pillars || [];
  const closing = sections.closing_invocation || {};

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[hsl(164,28%,14%)]">
        <div className="orbital-drift">
          <ConcentricRings size={80} opacity={0.3} />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <SiteHeader />

      {/* ── HERO ────────────────────────────────────── */}
      <section className="hero-gradient relative min-h-[90vh] flex items-center overflow-hidden" data-testid="hero-section">
        <ParticleCanvas />
        {/* Starfield overlay */}
        <div
          className="absolute inset-0 opacity-[0.08] bg-cover bg-center"
          style={{ backgroundImage: `url('https://images.unsplash.com/photo-1658237775407-aed6450f5851?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85')` }}
        />
        <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <motion.div
              initial="hidden"
              animate="visible"
              variants={{ visible: { transition: { staggerChildren: 0.15 } } }}
              className="relative z-10"
            >
              <motion.h1
                variants={fadeUp}
                className="font-['Cormorant_Garamond'] text-4xl sm:text-5xl lg:text-[68px] font-bold leading-[1.08] tracking-[-0.01em] text-[hsl(var(--foreground))] mb-10"
              >
                {hero.headline || 'The Cosmic Synthesist'}
              </motion.h1>
              <motion.div variants={fadeUp} className="flex items-center gap-4">
                <Link to={hero.cta_link || '/work'}>
                  <Button
                    className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-['Inter'] text-sm font-medium px-8 py-6 rounded-lg hover:brightness-[1.03] hover:shadow-[0_0_0_1px_rgba(201,168,76,0.25),0_18px_60px_rgba(0,0,0,0.45)] active:scale-[0.98] transition-all duration-200"
                    data-testid="hero-cta"
                  >
                    {hero.cta_text || 'Explore the Work'}
                  </Button>
                </Link>
              </motion.div>
            </motion.div>

            {/* Orbital diagram - visible on all screens */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5, duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
              className="absolute inset-0 flex items-center justify-center lg:relative lg:inset-auto pointer-events-none lg:pointer-events-auto"
              style={{ top: '0', left: '0' }}
            >
              {/* Mobile/tablet: centered on the text block area */}
              <div className="relative w-[320px] h-[320px] sm:w-[380px] sm:h-[380px] lg:w-[360px] lg:h-[360px] opacity-40 sm:opacity-45 lg:opacity-100 lg:translate-x-0 lg:translate-y-0">
                <svg className="absolute inset-0 w-full h-full concentric-ring" viewBox="0 0 360 360" fill="none">
                  {/* Concentric bullseye rings */}
                  <circle cx="180" cy="180" r="170" stroke="#C9A84C" strokeWidth="0.7" opacity="0.5" />
                  <circle cx="180" cy="180" r="130" stroke="#A8B5B2" strokeWidth="0.5" opacity="0.4" />
                  <circle cx="180" cy="180" r="90" stroke="#C9A84C" strokeWidth="0.6" opacity="0.45" />
                  <circle cx="180" cy="180" r="50" stroke="#A8B5B2" strokeWidth="0.5" opacity="0.35" />
                  <circle cx="180" cy="180" r="10" fill="#C9A84C" opacity="0.6" />

                  {/* Elliptical orbits - tilted, varied, stronger */}
                  <ellipse cx="180" cy="180" rx="158" ry="72" stroke="#C9A84C" strokeWidth="0.6" opacity="0.35" transform="rotate(-22 180 180)" strokeDasharray="4 8" />
                  <ellipse cx="180" cy="180" rx="125" ry="55" stroke="#D4B483" strokeWidth="0.5" opacity="0.3" transform="rotate(40 180 180)" />
                  <ellipse cx="180" cy="180" rx="100" ry="42" stroke="#C9A84C" strokeWidth="0.5" opacity="0.28" transform="rotate(-55 180 180)" strokeDasharray="2 6" />
                  <ellipse cx="180" cy="180" rx="145" ry="95" stroke="#A8B5B2" strokeWidth="0.4" opacity="0.2" transform="rotate(12 180 180)" />
                  <ellipse cx="180" cy="180" rx="80" ry="160" stroke="#C9A84C" strokeWidth="0.35" opacity="0.18" transform="rotate(-8 180 180)" />

                  {/* Axis lines - subtle crosshairs */}
                  <line x1="180" y1="8" x2="180" y2="352" stroke="#A8B5B2" strokeWidth="0.3" opacity="0.12" />
                  <line x1="8" y1="180" x2="352" y2="180" stroke="#A8B5B2" strokeWidth="0.3" opacity="0.12" />
                  <line x1="52" y1="52" x2="308" y2="308" stroke="#A8B5B2" strokeWidth="0.2" opacity="0.08" />
                  <line x1="308" y1="52" x2="52" y2="308" stroke="#A8B5B2" strokeWidth="0.2" opacity="0.08" />

                  {/* Degree tick marks on outer ring */}
                  {[0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330].map(deg => {
                    const rad = (deg * Math.PI) / 180;
                    const x1 = 180 + 166 * Math.cos(rad);
                    const y1 = 180 + 166 * Math.sin(rad);
                    const x2 = 180 + 174 * Math.cos(rad);
                    const y2 = 180 + 174 * Math.sin(rad);
                    return <line key={deg} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#C9A84C" strokeWidth="0.5" opacity="0.4" />;
                  })}

                  {/* Orbital bodies - planets/nodes */}
                  <circle cx="180" cy="10" r="3.5" fill="#C9A84C" opacity="0.75" />
                  <circle cx="180" cy="10" r="6" stroke="#C9A84C" strokeWidth="0.3" fill="none" opacity="0.3" />
                  <circle cx="312" cy="155" r="2.5" fill="#D4B483" opacity="0.65" />
                  <circle cx="312" cy="155" r="5" stroke="#D4B483" strokeWidth="0.3" fill="none" opacity="0.25" />
                  <circle cx="85" cy="275" r="2.8" fill="#C9A84C" opacity="0.55" />
                  <circle cx="262" cy="78" r="2" fill="#A8B5B2" opacity="0.6" />
                  <circle cx="262" cy="78" r="4" stroke="#A8B5B2" strokeWidth="0.3" fill="none" opacity="0.2" />
                  <circle cx="65" cy="125" r="1.8" fill="#C9A84C" opacity="0.5" />
                  <circle cx="235" cy="305" r="2.5" fill="#D4B483" opacity="0.5" />
                  <circle cx="110" cy="55" r="1.5" fill="#A8B5B2" opacity="0.45" />
                  <circle cx="295" cy="250" r="2" fill="#C9A84C" opacity="0.4" />
                  <circle cx="48" cy="195" r="1.8" fill="#D4B483" opacity="0.45" />

                  {/* Center glow */}
                  <circle cx="180" cy="180" r="18" fill="url(#centerGlow)" opacity="0.4" />
                  <circle cx="180" cy="180" r="4" fill="#C9A84C" opacity="0.8" />

                  <defs>
                    <radialGradient id="centerGlow">
                      <stop offset="0%" stopColor="#C9A84C" stopOpacity="0.6" />
                      <stop offset="100%" stopColor="#C9A84C" stopOpacity="0" />
                    </radialGradient>
                  </defs>
                </svg>
              </div>
            </motion.div>
          </div>

          {/* Scroll hint */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 scroll-hint">
            <ChevronDown className="h-5 w-5 text-[hsl(var(--silver))]" />
          </div>
        </div>
      </section>

      {/* ── IDENTITY ANCHOR ──────────────────────────── */}
      <section className="py-20 sm:py-32 relative" data-testid="identity-anchor">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
          >
            <motion.div variants={fadeUp} className="flex justify-center mb-8">
              <ConcentricRings size={60} opacity={0.15} />
            </motion.div>
            <motion.h2
              variants={fadeUp}
              className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl lg:text-4xl font-bold leading-[1.15] text-[hsl(var(--foreground))] mb-6"
            >
              {anchor.statement || 'You have always synthesized.'}
            </motion.h2>
            <motion.p
              variants={fadeUp}
              className="font-['Inter'] text-base sm:text-lg leading-[1.7] text-[hsl(var(--muted-foreground))] max-w-2xl mx-auto"
            >
              {anchor.description || 'The Cosmic Synthesist develops structured frameworks for understanding consciousness, identity, and power.'}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* ── THREE PILLARS ────────────────────────────── */}
      <section className="py-16 sm:py-24 section-mist" data-testid="three-pillars">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
          >
            <motion.p
              variants={fadeUp}
              className="font-['Inter'] text-xs font-medium uppercase tracking-[0.14em] text-[hsl(var(--silver))] mb-4"
            >
              The Three Pillars
            </motion.p>
            <motion.h2
              variants={fadeUp}
              className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold text-[hsl(var(--foreground))] mb-12 sm:mb-16"
            >
              The Foundation
            </motion.h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {pillars.map((pillar, i) => (
              <motion.div
                key={pillar.label || `pillar-${i}`}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-60px' }}
                custom={i}
                variants={fadeUp}
                className="group relative rounded-xl bg-[hsl(var(--card))] border border-[rgba(201,168,76,0.15)] p-6 sm:p-8 transition-all duration-500 hover:border-[rgba(201,168,76,0.35)] hover:bg-[rgba(27,77,68,0.12)]"
                data-testid={`pillar-${i}`}
              >
                {/* Blueprint overlay on hover */}
                <div className="absolute inset-0 rounded-xl blueprint-grid opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" />
                <div className="relative z-10">
                  <p className="font-['Inter'] text-[10px] font-medium uppercase tracking-[0.14em] text-[hsl(var(--gold))] mb-4">
                    {pillar.label}
                  </p>
                  <h3 className="font-['Cormorant_Garamond'] text-xl sm:text-2xl font-semibold italic text-[hsl(var(--foreground))] mb-4 leading-[1.2]">
                    {pillar.title}
                  </h3>
                  <p className="font-['Inter'] text-sm leading-[1.7] text-[hsl(var(--muted-foreground))]">
                    {pillar.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── SACRED FEMININE QUIZ CTA ──────────────────── */}
      <section className="py-16 sm:py-20" data-testid="quiz-cta">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
          >
            <div className="rounded-xl border border-[rgba(201,168,76,0.2)] bg-[hsl(var(--card))] p-8 sm:p-12 flex flex-col lg:flex-row items-center gap-8 lg:gap-12 shadow-[0_18px_60px_rgba(0,0,0,0.35)]">
              {/* Left - spectrum dots */}
              <div className="hidden lg:flex flex-col items-center gap-3 shrink-0">
                {['#7C6A52', '#6B7A5C', '#7B6E9E', '#5E7EA8', '#9B8EC4'].map((c) => (
                  <div key={c} className="w-3 h-3 rounded-full" style={{ background: c }} />
                ))}
              </div>
              <div className="flex-1 text-center lg:text-left">
                <motion.p variants={fadeUp} className="font-['Inter'] text-[10px] font-medium uppercase tracking-[0.18em] text-[hsl(var(--gold))] mb-3">
                  Free Archetype Assessment
                </motion.p>
                <motion.h2 variants={fadeUp} className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl font-bold text-[hsl(var(--foreground))] mb-3 leading-[1.15]">
                  Discover Your Sacred Feminine Thinking Archetype
                </motion.h2>
                <motion.p variants={fadeUp} className="font-['Inter'] text-sm leading-[1.7] text-[hsl(var(--muted-foreground))] max-w-lg">
                  Are you the Cultivator, the Weaver, the Priestess, the Seeker, or the Dreamer? A 5-minute quiz to reveal how you lead, create, and make sense of the world.
                </motion.p>
              </div>
              <motion.div variants={fadeUp} className="shrink-0">
                <Link to="/sacred-feminine-quiz">
                  <Button className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-['Inter'] text-sm font-medium px-8 py-6 rounded-lg hover:brightness-[1.03] whitespace-nowrap">
                    Take the Quiz
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ── FRAMEWORK LIBRARY PREVIEW ────────────────── */}
      <section className="py-16 sm:py-24" data-testid="frameworks-preview">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
            className="flex items-end justify-between mb-12 sm:mb-16"
          >
            <div>
              <motion.p
                variants={fadeUp}
                className="font-['Inter'] text-xs font-medium uppercase tracking-[0.14em] text-[hsl(var(--silver))] mb-4"
              >
                Framework Library
              </motion.p>
              <motion.h2
                variants={fadeUp}
                className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold text-[hsl(var(--foreground))]"
              >
                The Work
              </motion.h2>
            </div>
            <motion.div variants={fadeUp}>
              <Link
                to="/work"
                className="hidden sm:inline-flex items-center gap-2 font-['Inter'] text-sm text-[hsl(var(--gold))] hover:text-[hsl(var(--champagne))] transition-colors duration-300"
              >
                View all frameworks
                <ArrowRight className="h-4 w-4" />
              </Link>
            </motion.div>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {frameworks.slice(0, 6).map((fw, i) => (
              <motion.div
                key={fw._id || fw.slug}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-40px' }}
                custom={i}
                variants={fadeUp}
              >
                <FrameworkCard framework={fw} />
              </motion.div>
            ))}
          </div>

          <div className="sm:hidden mt-8 text-center">
            <Link
              to="/work"
              className="inline-flex items-center gap-2 font-['Inter'] text-sm text-[hsl(var(--gold))] hover:text-[hsl(var(--champagne))] transition-colors"
            >
              View all frameworks
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── ORACLE DECK PRODUCT BLOCK ────────────────── */}
      {product && (
        <section className="py-16 sm:py-24 section-mist" data-testid="oracle-deck-block">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
              {/* Product image placeholder */}
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                className="relative aspect-square max-w-md mx-auto lg:mx-0 rounded-xl overflow-hidden border border-[rgba(201,168,76,0.25)] shadow-[0_18px_60px_rgba(0,0,0,0.5)]"
              >
                <div className="absolute inset-0 hero-gradient flex items-center justify-center">
                  <div className="text-center">
                    <ConcentricRings size={160} opacity={0.2} />
                    <p className="font-['Cormorant_Garamond'] text-2xl font-bold text-[hsl(var(--foreground))] mt-4">
                      The Oracle Deck
                    </p>
                    <p className="font-['Inter'] text-xs uppercase tracking-[0.14em] text-[hsl(var(--gold))] mt-2">
                      Coming Soon
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Product info */}
              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
              >
                <motion.p
                  variants={fadeUp}
                  className="font-['Inter'] text-xs font-medium uppercase tracking-[0.14em] text-[hsl(var(--gold))] mb-4"
                >
                  Flagship Product
                </motion.p>
                <motion.h2
                  variants={fadeUp}
                  className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold text-[hsl(var(--foreground))] mb-3"
                >
                  {product.name}
                </motion.h2>
                <motion.p
                  variants={fadeUp}
                  className="font-['Cormorant_Garamond'] text-xl italic text-[hsl(var(--champagne))] mb-6"
                >
                  {product.tagline}
                </motion.p>
                <motion.p
                  variants={fadeUp}
                  className="font-['Inter'] text-[15px] leading-[1.7] text-[hsl(var(--muted-foreground))] mb-8"
                >
                  {product.description}
                </motion.p>

                {product.prelaunch ? (
                  <motion.form variants={fadeUp} onSubmit={handleWaitlist} className="space-y-4">
                    <p className="font-['Inter'] text-sm text-[hsl(var(--silver))]">
                      {product.prelaunch_message}
                    </p>
                    <div className="flex gap-3">
                      <Input
                        type="email"
                        value={waitlistEmail}
                        onChange={(e) => setWaitlistEmail(e.target.value)}
                        placeholder="Your email address"
                        className="bg-[hsl(var(--card))] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:ring-[hsl(var(--gold))] focus:border-[rgba(201,168,76,0.4)] font-['Inter'] text-sm"
                        data-testid="waitlist-email-input"
                        required
                      />
                      <Button
                        type="submit"
                        className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-['Inter'] text-sm font-medium px-6 rounded-lg hover:brightness-[1.03] shrink-0"
                        data-testid="waitlist-submit-button"
                      >
                        Join Waitlist
                      </Button>
                    </div>
                  </motion.form>
                ) : (
                  <motion.div variants={fadeUp}>
                    <Link to="/oracle-deck">
                      <Button className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-['Inter'] text-sm font-medium px-8 py-6 rounded-lg">
                        View the Deck
                      </Button>
                    </Link>
                  </motion.div>
                )}
              </motion.div>
            </div>
          </div>
        </section>
      )}

      {/* ── ESSAYS PREVIEW ────────────────────────────── */}
      <section className="py-16 sm:py-24" data-testid="essays-preview">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-80px' }}
            variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
            className="flex items-end justify-between mb-12 sm:mb-16"
          >
            <div>
              <motion.p
                variants={fadeUp}
                className="font-['Inter'] text-xs font-medium uppercase tracking-[0.14em] text-[hsl(var(--silver))] mb-4"
              >
                Writing Portal
              </motion.p>
              <motion.h2
                variants={fadeUp}
                className="font-['Cormorant_Garamond'] text-3xl sm:text-4xl font-bold text-[hsl(var(--foreground))]"
              >
                Recent Transmissions
              </motion.h2>
            </div>
            <motion.div variants={fadeUp}>
              <Link
                to="/essays"
                className="hidden sm:inline-flex items-center gap-2 font-['Inter'] text-sm text-[hsl(var(--gold))] hover:text-[hsl(var(--champagne))] transition-colors duration-300"
              >
                All essays
                <ArrowRight className="h-4 w-4" />
              </Link>
            </motion.div>
          </motion.div>

          <div className="space-y-0">
            {essays.map((essay, i) => (
              <motion.div
                key={essay._id || essay.slug}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: '-40px' }}
                custom={i}
                variants={fadeUp}
              >
                <EssayCard essay={essay} />
              </motion.div>
            ))}
          </div>

          <div className="sm:hidden mt-8 text-center">
            <Link
              to="/essays"
              className="inline-flex items-center gap-2 font-['Inter'] text-sm text-[hsl(var(--gold))] hover:text-[hsl(var(--champagne))] transition-colors"
            >
              All essays
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ── CLOSING INVOCATION ────────────────────────── */}
      <section className="py-20 sm:py-32 section-mist" data-testid="closing-invocation">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={{ visible: { transition: { staggerChildren: 0.15 } } }}
          >
            <motion.div variants={fadeUp} className="flex justify-center mb-8">
              <ConcentricRings size={50} opacity={0.12} />
            </motion.div>
            <motion.blockquote
              variants={fadeUp}
              className="font-['Cormorant_Garamond'] text-2xl sm:text-3xl lg:text-4xl font-normal italic leading-[1.3] text-[hsl(var(--foreground))] mb-10"
            >
              {closing.text || 'The work does not wait for you to be ready. It has always been here.'}
            </motion.blockquote>
            <motion.p
              variants={fadeUp}
              className="font-['Inter'] text-sm text-[hsl(var(--muted-foreground))] mb-8"
            >
              {closing.cta_description || 'Receive structured transmissions on consciousness, identity, and the architecture of power.'}
            </motion.p>
            <motion.form variants={fadeUp} onSubmit={handleNewsletter} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email address"
                className="bg-[hsl(var(--card))] border-[rgba(201,168,76,0.2)] text-[hsl(var(--foreground))] placeholder:text-[hsl(var(--muted-foreground))] focus:ring-[hsl(var(--gold))] focus:border-[rgba(201,168,76,0.4)] font-['Inter'] text-sm"
                data-testid="newsletter-email-input"
                required
              />
              <Button
                type="submit"
                className="bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] font-['Inter'] text-sm font-medium px-6 rounded-lg hover:brightness-[1.03] shrink-0"
                data-testid="newsletter-submit-button"
              >
                {closing.cta_text || 'Join the Observatory'}
              </Button>
            </motion.form>
          </motion.div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
